import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Globe, Check, ArrowRight, ArrowLeft, Loader2, Save, FileSearch, Edit2, Users, Plus, X, ExternalLink, CreditCard, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import api from '../lib/api';

const steps = [
  { id: 'subscription', label: 'Abo wählen', icon: CreditCard },
  { id: 'url', label: 'Firmen-URL', icon: Globe },
  { id: 'profile', label: 'Profil prüfen', icon: Edit2 },
  { id: 'icp', label: 'Wunschkunde', icon: Users },
];

// Pre-defined options for ICP assessment
const REGION_OPTIONS = ['DACH', 'Deutschland', 'Österreich', 'Schweiz', 'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland', 'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen'];
const SIZE_OPTIONS = ['1-10 (Kleinstunternehmen)', '11-50 (Kleinunternehmen)', '51-250 (Mittelstand)', '251-1000 (Großer Mittelstand)', '1000+ (Konzern)'];
const CUSTOMER_TYPE_OPTIONS = ['Produzierendes Gewerbe', 'Dienstleister', 'Handel', 'Öffentliche Einrichtungen', 'Startups & Tech', 'Handwerk'];

function TagSelector({ label, description, options, selected, onChange, suggestedTags, allowCustom, testId }) {
  const [customInput, setCustomInput] = useState('');

  const toggle = (tag) => {
    if (selected.includes(tag)) {
      onChange(selected.filter(t => t !== tag));
    } else {
      onChange([...selected, tag]);
    }
  };

  const addCustom = () => {
    const val = customInput.trim();
    if (val && !selected.includes(val) && !options.includes(val)) {
      onChange([...selected, val]);
      setCustomInput('');
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="text-sm font-semibold block">{label}</label>
      {description && <p className="text-xs text-muted-foreground -mt-1">{description}</p>}

      {/* AI-suggested tags (highlighted) */}
      {suggestedTags && suggestedTags.length > 0 && (
        <div className="mb-1">
          <span className="text-xs text-primary font-medium">Vorschläge basierend auf Ihrer Website:</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {suggestedTags.filter(t => !options.includes(t)).map(tag => (
              <Badge
                key={tag}
                variant={selected.includes(tag) ? 'default' : 'outline'}
                className={`cursor-pointer transition-all text-xs ${selected.includes(tag) ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' : 'border-primary/40 text-primary hover:bg-primary/10'}`}
                onClick={() => toggle(tag)}
                data-testid={`suggested-tag-${tag}`}
              >
                {selected.includes(tag) && <Check className="w-3 h-3 mr-1" />}
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Standard options */}
      <div className="flex flex-wrap gap-1.5">
        {options.map(tag => (
          <Badge
            key={tag}
            variant={selected.includes(tag) ? 'default' : 'outline'}
            className={`cursor-pointer transition-all text-xs ${selected.includes(tag) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            onClick={() => toggle(tag)}
            data-testid={`tag-${tag}`}
          >
            {selected.includes(tag) && <Check className="w-3 h-3 mr-1" />}
            {tag}
          </Badge>
        ))}
      </div>

      {/* Custom tags already added */}
      {selected.filter(t => !options.includes(t) && !(suggestedTags || []).includes(t)).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.filter(t => !options.includes(t) && !(suggestedTags || []).includes(t)).map(tag => (
            <Badge key={tag} className="bg-primary text-primary-foreground text-xs">
              {tag}
              <X className="w-3 h-3 ml-1 cursor-pointer" onClick={(e) => { e.stopPropagation(); onChange(selected.filter(t => t !== tag)); }} />
            </Badge>
          ))}
        </div>
      )}

      {/* Custom input */}
      {allowCustom && (
        <div className="flex gap-2 mt-1">
          <Input
            placeholder="Eigenen Wert hinzufügen..."
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustom())}
            className="h-8 text-xs"
            data-testid={`${testId}-custom-input`}
          />
          <Button variant="outline" size="sm" onClick={addCustom} disabled={!customInput.trim()} className="h-8 px-2" data-testid={`${testId}-add-btn`}>
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function OnboardingPage() {
  const { refreshCompany } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [url, setUrl] = useState('');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipPassword, setSkipPassword] = useState('');
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceData, setInvoiceData] = useState({ company_name: '', street: '', zip: '', city: '', contact_person: '', email: '', ust_id: '' });
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoicePlanId, setInvoicePlanId] = useState(null);

  // ICP state
  const [icpRegions, setIcpRegions] = useState([]);
  const [icpSizes, setIcpSizes] = useState([]);
  const [icpIndustries, setIcpIndustries] = useState([]);
  const [icpCustomerTypes, setIcpCustomerTypes] = useState([]);

  // AI suggestions from URL analysis
  const [suggestions, setSuggestions] = useState({});

  useEffect(() => {
    // Check for Stripe checkout return
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      // Remove session_id from URL
      window.history.replaceState({}, '', '/onboarding');
      api.get(`/billing/checkout-status/${sessionId}`).then(res => {
        if (res.data.status === 'active') {
          toast.success('Zahlung erfolgreich! Abo aktiviert.');
          setHasSubscription(true);
          setStep(1); // Go to URL step
        }
      }).catch(() => {});
    }

    // Check current subscription status and load plans
    Promise.all([
      api.get('/auth/me'),
      api.get('/plans'),
      api.get('/company/profile').catch(() => ({ data: {} })),
    ]).then(([meRes, plansRes, profileRes]) => {
      const company = meRes.data.company || {};
      const isActive = company.subscription_status === 'active' || company.is_demo;
      setHasSubscription(isActive);
      if (isActive) setStep(1); // Skip subscription step
      setPlans((plansRes.data.plans || plansRes.data || []).filter(p => p.id !== 'free'));
      if (profileRes.data.profile) {
        setProfile(profileRes.data.profile);
        setUrl(profileRes.data.profile.source_url || '');
        if (profileRes.data.profile.icp_suggestions) {
          setSuggestions(profileRes.data.profile.icp_suggestions);
        }
      }
    }).catch(() => {}).finally(() => setPlansLoading(false));
  }, []);

  const startCheckout = async (planId, interval = 'monthly') => {
    setCheckoutLoading(planId);
    try {
      const res = await api.post('/billing/checkout', {
        plan_id: planId,
        interval,
        origin_url: window.location.origin,
        return_path: '/onboarding',
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout konnte nicht gestartet werden');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const openInvoiceDialog = (planId) => {
    setInvoicePlanId(planId);
    setShowInvoiceDialog(true);
  };

  const submitInvoiceOrder = async () => {
    const { company_name, street, zip, city, contact_person, email } = invoiceData;
    if (!company_name || !street || !zip || !city || !contact_person || !email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    setInvoiceLoading(true);
    try {
      const res = await api.post('/billing/invoice-order', {
        plan_id: invoicePlanId,
        billing_data: invoiceData,
      });
      toast.success('Bestellung auf Rechnung erfolgreich! Ihr Abo wird nach Zahlungseingang aktiviert.');
      setShowInvoiceDialog(false);
      setHasSubscription(true);
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bestellung konnte nicht aufgegeben werden');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const analyzeUrl = async () => {
    if (!url.trim()) { toast.error('Bitte geben Sie eine URL ein'); return; }
    setLoading(true);
    try {
      const res = await api.post('/company/submit-url', { url: url.trim() });
      const p = res.data.profile;
      setProfile(p);
      // Extract AI suggestions and pre-select them
      if (p.icp_suggestions) {
        setSuggestions(p.icp_suggestions);
        setIcpRegions(p.icp_suggestions.target_regions || []);
        setIcpSizes(p.icp_suggestions.target_company_sizes || []);
        setIcpIndustries(p.icp_suggestions.target_industries || []);
        setIcpCustomerTypes(p.icp_suggestions.target_customer_types || []);
      }
      toast.success('Website erfolgreich analysiert!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'URL konnte nicht analysiert werden');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await api.put('/company/profile', profile);
      toast.success('Profil gespeichert!');
      setStep(3);
    } catch {
      toast.error('Profil konnte nicht aktualisiert werden');
    } finally {
      setLoading(false);
    }
  };

  const saveIcpAndFinish = async () => {
    setLoading(true);
    try {
      await api.put('/company/icp-assessment', {
        target_region: icpRegions.join(', '),
        target_company_size: icpSizes.join(', '),
        target_industry: icpIndustries.join(', '),
        target_customer_type: icpCustomerTypes.join(', '),
        custom_tags: [
          ...icpRegions.filter(t => !REGION_OPTIONS.includes(t)),
          ...icpIndustries.filter(t => !(suggestions.target_industries || []).includes(t)),
        ],
      });
      await refreshCompany();
      toast.success('Wunschkunden-Profil gespeichert! Ihr Dashboard wird geladen.');
      navigate('/dashboard');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="max-w-2xl mx-auto p-6" data-testid="onboarding-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Einrichtung</h1>
        <p className="text-muted-foreground">Konfigurieren Sie Ihr Firmenprofil, um mit der Kundenrecherche zu starten</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div className={`flex-1 h-0.5 ${i <= step ? 'bg-primary' : 'bg-border'}`} />}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${done ? 'bg-primary/10 text-primary' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 0: Subscription Gate */}
      {step === 0 && (
        <Card data-testid="subscription-step">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-600" /> Abo-Plan wählen</CardTitle>
            <CardDescription>Um Ihr Unternehmen zu registrieren und Leads zu finden, wählen Sie bitte einen passenden Plan.</CardDescription>
          </CardHeader>
          <CardContent>
            {plansLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  {plans.map(plan => (
                    <div key={plan.id} className={`relative border rounded-xl p-5 transition-all hover:border-emerald-500/50 hover:shadow-md ${plan.id === 'pro' ? 'border-emerald-500 ring-1 ring-emerald-500/20' : 'border-border'}`}>
                      {plan.id === 'pro' && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-semibold px-3 py-0.5 rounded-full">Empfohlen</div>
                      )}
                      <h3 className="font-semibold text-lg">{plan.name}</h3>
                      <div className="mt-2 flex items-baseline gap-1">
                        <span className="text-3xl font-bold">{plan.monthly_eur || plan.price_monthly || plan.price}€</span>
                        <span className="text-sm text-muted-foreground">/Monat</span>
                      </div>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> {plan.max_leads_per_month} Leads/Monat</li>
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> {plan.max_users} Nutzer</li>
                      </ul>
                      <Button
                        className={`w-full mt-5 ${plan.id === 'pro' ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : ''}`}
                        variant={plan.id === 'pro' ? 'default' : 'outline'}
                        disabled={checkoutLoading !== null}
                        onClick={() => startCheckout(plan.id)}
                        data-testid={`checkout-plan-${plan.id}`}
                      >
                        {checkoutLoading === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-3.5 h-3.5 mr-1.5" /> Kreditkarte</>}
                      </Button>
                      <Button
                        className="w-full mt-2"
                        variant="ghost"
                        size="sm"
                        disabled={checkoutLoading !== null}
                        onClick={() => openInvoiceDialog(plan.id)}
                        data-testid={`invoice-plan-${plan.id}`}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> Auf Rechnung bestellen
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="text-center pt-2">
                  <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setShowSkipDialog(true)} data-testid="skip-subscription-btn">
                    <Clock className="w-3.5 h-3.5 mr-1.5" /> Später abonnieren
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 1: URL */}
      {step === 1 && (
        <Card data-testid="url-step">
          <CardHeader>
            <CardTitle>Website Ihres Unternehmens</CardTitle>
            <CardDescription>Geben Sie Ihre Firmen-URL ein. Wir analysieren Ihre Website und erstellen automatisch ein Unternehmensprofil.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input placeholder="https://www.ihre-firma.de" value={url} onChange={e => setUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && analyzeUrl()} data-testid="url-input" />
              <Button onClick={analyzeUrl} disabled={loading} data-testid="analyze-url-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2">Analysieren <ArrowRight className="w-4 h-4" /></span>}
              </Button>
            </div>
            {loading && (
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/10 mt-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Website wird analysiert...</p>
                    <p className="text-xs text-muted-foreground">Geschäftsinformationen werden per KI extrahiert</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Profile Review */}
      {step === 2 && profile && (
        <Card data-testid="profile-step">
          <CardHeader>
            <CardTitle>Ihr Unternehmensprofil</CardTitle>
            <CardDescription>Prüfen Sie die KI-generierten Informationen und passen Sie diese bei Bedarf an.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { label: 'Branche', field: 'industry' },
                { label: 'Wertversprechen', field: 'value_proposition' },
                { label: 'Beschreibung', field: 'company_description' },
              ].map(({ label, field }) => (
                <div key={field}>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">{label}</label>
                  <Textarea value={profile[field] || ''} onChange={e => setProfile(p => ({ ...p, [field]: e.target.value }))} rows={field === 'company_description' ? 3 : 2} />
                </div>
              ))}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Produkte & Services</label>
                <div className="flex flex-wrap gap-2">
                  {(profile.products_services || []).map((p, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Schlüsselwörter</label>
                <div className="flex flex-wrap gap-2">
                  {(profile.keywords || []).map((k, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{k}</Badge>
                  ))}
                </div>
              </div>
              {profile.crawled_pages && profile.crawled_pages.length > 0 && (
                <div data-testid="crawled-pages-section">
                  <label className="text-sm font-medium text-muted-foreground mb-2 block flex items-center gap-1.5">
                    <FileSearch className="w-4 h-4" />Analysierte Seiten ({profile.crawled_pages.length})
                  </label>
                  <div className="rounded-lg border border-border/50 divide-y divide-border/50">
                    {profile.crawled_pages.map((page, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`crawled-page-${i}`}>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${page.status?.includes('ok') ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                          {page.status?.includes('ok')
                            ? <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                            : <span className="w-2 h-2 rounded-full bg-red-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{page.title || page.url}</p>
                          <p className="text-xs text-muted-foreground truncate">{page.url}</p>
                        </div>
                        <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary shrink-0">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(0)} data-testid="back-to-url-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
              </Button>
              <Button onClick={saveProfile} disabled={loading} data-testid="save-profile-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2">Weiter <ArrowRight className="w-4 h-4" /></span>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: ICP Assessment */}
      {step === 3 && (
        <Card data-testid="icp-step">
          <CardHeader>
            <CardTitle>Wunschkunden-Profil</CardTitle>
            <CardDescription>Definieren Sie Ihren idealen Kunden. Klicken Sie auf die vorgeschlagenen Tags oder fügen Sie eigene hinzu.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <TagSelector
                label="Zielregion"
                description="Wo sollen Ihre Kunden sitzen?"
                options={REGION_OPTIONS}
                selected={icpRegions}
                onChange={setIcpRegions}
                suggestedTags={suggestions.target_regions}
                allowCustom
                testId="icp-region"
              />

              <TagSelector
                label="Unternehmensgröße"
                description="Wie groß sollen die Zielunternehmen sein?"
                options={SIZE_OPTIONS}
                selected={icpSizes}
                onChange={setIcpSizes}
                suggestedTags={suggestions.target_company_sizes}
                allowCustom={false}
                testId="icp-size"
              />

              <TagSelector
                label="Zielbranche"
                description="In welcher Branche sind Ihre idealen Kunden?"
                options={[]}
                selected={icpIndustries}
                onChange={setIcpIndustries}
                suggestedTags={suggestions.target_industries}
                allowCustom
                testId="icp-industry"
              />

              <TagSelector
                label="Kundentyp"
                description="Welche Art von Unternehmen?"
                options={CUSTOMER_TYPE_OPTIONS}
                selected={icpCustomerTypes}
                onChange={setIcpCustomerTypes}
                suggestedTags={suggestions.target_customer_types}
                allowCustom={false}
                testId="icp-customer-type"
              />
            </div>

            <div className="flex justify-between pt-6">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-profile-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
              </Button>
              <Button onClick={saveIcpAndFinish} disabled={loading} data-testid="finish-onboarding-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2"><Save className="w-4 h-4" /> Speichern & Starten</span>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>

    <Dialog open={showSkipDialog} onOpenChange={v => { setShowSkipDialog(v); setSkipPassword(''); }}>
      <DialogContent className="sm:max-w-md" data-testid="skip-password-dialog">
        <DialogHeader>
          <DialogTitle>Zahlung überspringen</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">Diese Funktion ist nur für autorisierte Nutzer. Bitte geben Sie das Passwort ein.</p>
        <Input
          type="password"
          placeholder="Passwort eingeben"
          value={skipPassword}
          onChange={e => setSkipPassword(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              if (skipPassword === 'laizy2026!') { setShowSkipDialog(false); setSkipPassword(''); setStep(1); }
              else { toast.error('Falsches Passwort'); }
            }
          }}
          data-testid="skip-password-input"
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => { setShowSkipDialog(false); setSkipPassword(''); }}>Abbrechen</Button>
          <Button onClick={() => {
            if (skipPassword === 'laizy2026!') { setShowSkipDialog(false); setSkipPassword(''); setStep(1); }
            else { toast.error('Falsches Passwort'); }
          }} data-testid="skip-password-confirm-btn">Bestätigen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Rechnungs-Dialog */}
    <Dialog open={showInvoiceDialog} onOpenChange={v => setShowInvoiceDialog(v)}>
      <DialogContent className="sm:max-w-lg" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Auf Rechnung bestellen</DialogTitle>
          <DialogDescription>Bitte geben Sie Ihre Rechnungsdaten ein. Ihr Abonnement wird nach Zahlungseingang aktiviert.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm font-medium">Firmenname *</Label>
            <Input placeholder="Muster GmbH" value={invoiceData.company_name} onChange={e => setInvoiceData(d => ({...d, company_name: e.target.value}))} data-testid="invoice-company-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Ansprechpartner *</Label>
              <Input placeholder="Max Mustermann" value={invoiceData.contact_person} onChange={e => setInvoiceData(d => ({...d, contact_person: e.target.value}))} data-testid="invoice-contact" />
            </div>
            <div>
              <Label className="text-sm font-medium">E-Mail für Rechnung *</Label>
              <Input type="email" placeholder="buchhaltung@firma.de" value={invoiceData.email} onChange={e => setInvoiceData(d => ({...d, email: e.target.value}))} data-testid="invoice-email" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Straße + Hausnummer *</Label>
            <Input placeholder="Musterstraße 1" value={invoiceData.street} onChange={e => setInvoiceData(d => ({...d, street: e.target.value}))} data-testid="invoice-street" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">PLZ *</Label>
              <Input placeholder="12345" value={invoiceData.zip} onChange={e => setInvoiceData(d => ({...d, zip: e.target.value}))} data-testid="invoice-zip" />
            </div>
            <div>
              <Label className="text-sm font-medium">Ort *</Label>
              <Input placeholder="Berlin" value={invoiceData.city} onChange={e => setInvoiceData(d => ({...d, city: e.target.value}))} data-testid="invoice-city" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">USt-IdNr. <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input placeholder="DE123456789" value={invoiceData.ust_id} onChange={e => setInvoiceData(d => ({...d, ust_id: e.target.value}))} data-testid="invoice-ust-id" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Abbrechen</Button>
          <Button onClick={submitInvoiceOrder} disabled={invoiceLoading} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="invoice-submit-btn">
            {invoiceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2"><FileText className="w-4 h-4" /> Jetzt kostenpflichtig bestellen</span>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
